/**
 * Rules and actions behind the unclaimed-credit queue, open returns, line voiding and
 * the auditor export, kept out of the components so they are testable without a DOM.
 *
 * Each rule mirrors the route it fronts (bounds, roles, what a refusal means); the
 * server still decides. An action here returns the server's own reason on refusal,
 * never a fixed sentence in its place.
 */
import type { BankTransaction, OpenReturn, VoidRequest } from '../../services/bankTransactions.service';
import type {
  ReviewOutcome,
  UnclaimedBand,
  UnclaimedCredit,
  UnclaimedReviewBody,
} from '../../services/ops.service';
import { getApiErrorMessage } from '../../lib/apiError';
import { MFA_MIN, isMfaCode, stepUpErrorMessage } from '../../lib/mfaApproval';

// ── Roles ────────────────────────────────────────────────────────────────

/** manager and super_admin. A missing role is NOT a manager: deny by default. */
export function isManagerRole(role: string | null): boolean {
  return role === 'manager' || role === 'super_admin';
}

// ── What a list says about itself ────────────────────────────────────────

export type ListView = 'loading' | 'error' | 'empty' | 'rows';

/** A list is empty, could not be read, or has rows; an unread list is never "empty". */
export function listView(state: {
  loading: boolean;
  error: string | null;
  count: number;
}): ListView {
  if (state.loading) return 'loading';
  if (state.error !== null) return 'error';
  return state.count === 0 ? 'empty' : 'rows';
}

// ── Step-up ──────────────────────────────────────────────────────────────

// ── Unclaimed credits ────────────────────────────────────────────────────

export const REVIEW_REASON_MIN = 10;
export const REVIEW_REASON_MAX = 2000;
export const QUIET_DAYS_MIN = 1;
export const QUIET_DAYS_MAX = 30;

export const OUTCOME_LABEL: Record<ReviewOutcome, string> = {
  waiting: 'Waiting — leave it quiet for a stated number of days',
  escalated: 'Escalate — a manager needs to look at it',
  return_decided: 'Return decided — the money should go back',
};

/** An agent may record a wait or an escalation; deciding a return is a manager's call. */
export function outcomesFor(role: string | null): ReviewOutcome[] {
  return isManagerRole(role)
    ? ['waiting', 'escalated', 'return_decided']
    : ['waiting', 'escalated'];
}

export const AGENT_RETURN_NOTE =
  'Deciding to send money back is a manager’s call, so that choice is not offered to you.';

export const RETURN_DECIDED_NOTE =
  'This records the decision only. The money leaves through "Send back" on the attribution '
  + 'page, which needs an authenticator code and a second manager to confirm it left.';

export const BAND_LABEL: Record<UnclaimedBand, string> = {
  fresh: 'Recent',
  unexplained: 'Unexplained',
  escalated: 'Escalated',
  overdue: 'Overdue',
  return_due: 'Return due',
};

export interface ReviewDraft {
  outcome: ReviewOutcome | '';
  reason: string;
  quietDays: string;
}

export const EMPTY_REVIEW: ReviewDraft = { outcome: '', reason: '', quietDays: '' };

/** The server collapses runs of whitespace before measuring the reason. */
export function collapseReason(reason: string): string {
  return reason.split(/\s+/).filter(Boolean).join(' ');
}

function wholeDays(raw: string): number | null {
  if (!/^\d+$/.test(raw.trim())) return null;
  return Number(raw.trim());
}

/** Why the review cannot be recorded yet, in words for the screen; null when it can. */
export function reviewProblem(draft: ReviewDraft, role: string | null): string | null {
  if (draft.outcome === '') return 'Choose what you found.';
  if (!outcomesFor(role).includes(draft.outcome)) return AGENT_RETURN_NOTE;
  const reason = collapseReason(draft.reason);
  if (reason.length < REVIEW_REASON_MIN) {
    return `Say what you found and what it is waiting on, in at least ${REVIEW_REASON_MIN} characters.`;
  }
  if (reason.length > REVIEW_REASON_MAX) {
    return `Keep the reason under ${REVIEW_REASON_MAX} characters.`;
  }
  if (draft.outcome === 'waiting') {
    const days = wholeDays(draft.quietDays);
    if (days === null || days < QUIET_DAYS_MIN || days > QUIET_DAYS_MAX) {
      return `Say how many days it waits, between ${QUIET_DAYS_MIN} and ${QUIET_DAYS_MAX}.`;
    }
  }
  return null;
}

/** The exact body the route receives. `quiet_days` goes only with `waiting`. */
export function buildReviewBody(draft: ReviewDraft): UnclaimedReviewBody {
  if (draft.outcome === '') throw new Error('A review needs an outcome.');
  const body: UnclaimedReviewBody = {
    outcome: draft.outcome,
    reason: collapseReason(draft.reason),
  };
  if (draft.outcome === 'waiting') body.quiet_days = Number(draft.quietDays.trim());
  return body;
}

/** What the screen confirms after a review is recorded. */
export function describeReview(result: {
  outcome: ReviewOutcome;
  quiet_until: string | null;
}): string {
  if (result.outcome === 'waiting') {
    return result.quiet_until
      ? `Recorded. It stays quiet until ${result.quiet_until}, then it ages again by itself.`
      : 'Recorded.';
  }
  if (result.outcome === 'escalated') return 'Recorded. A manager can now see it was escalated.';
  return 'Recorded the decision to send it back. Nothing has moved yet.';
}

/** Oldest first is the order the server sends; keep it, but never trust an unsorted list. */
export function oldestFirst(rows: UnclaimedCredit[]): UnclaimedCredit[] {
  return [...rows].sort((a, b) => b.age_days - a.age_days);
}

// ── Open returns ─────────────────────────────────────────────────────────

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isUuid(value: string): boolean {
  return UUID.test(value.trim());
}

export interface SettlementDraft {
  debitId: string;
  mfaCode: string;
}

export const SETTLEMENT_SOD_NOTE =
  'A different manager from the one who decided this return has to confirm it left. '
  + 'The list does not say who decided it, so if you did, the server will refuse.';

/** Why settlement cannot be confirmed yet; null when it can. */
export function settlementProblem(
  draft: SettlementDraft,
  role: string | null,
): string | null {
  if (!isManagerRole(role)) return 'Only a manager can confirm that a return left the account.';
  if (!isUuid(draft.debitId)) {
    return 'Pick the outgoing bank movement that is this return leaving.';
  }
  if (!isMfaCode(draft.mfaCode)) return `Enter your ${MFA_MIN}-digit authenticator code.`;
  return null;
}

export function settlementConfirmation(ret: OpenReturn): string {
  return (
    `This says the bank movement you picked is the return of ${String(ret.amount)} `
    + `${ret.currency_code} (less ${String(ret.fee_amount)} ${ret.fee_currency ?? ret.currency_code} `
    + 'charged by the bank) leaving the account. The nightly cash proof will then treat that '
    + 'debit as explained. It is written once and cannot be changed.'
  );
}

/** How long a return has been waiting to be seen leaving, from the decision date. */
export function oldestReturnFirst(rows: OpenReturn[]): OpenReturn[] {
  return [...rows].sort((a, b) => a.decided_at.localeCompare(b.decided_at));
}

// ── Voiding a line ───────────────────────────────────────────────────────

export const VOID_REASON_MIN = 5;
export const VOID_REASON_MAX = 500;

export function voidProblem(reason: string): string | null {
  const length = reason.trim().length;
  if (length < VOID_REASON_MIN) {
    return `Say why, in at least ${VOID_REASON_MIN} characters.`;
  }
  if (length > VOID_REASON_MAX) return `Keep the reason under ${VOID_REASON_MAX} characters.`;
  return null;
}

export function voidConfirmation(tx: Pick<BankTransaction, 'amount' | 'currency_code'>): string {
  return (
    `This asks to mark the unmatched line of ${tx.amount} ${tx.currency_code} as not real money `
    + '(for example a duplicate capture). A manager other than you has to confirm it; once '
    + 'confirmed it leaves the queue and the cash proof, and that cannot be undone. Money '
    + 'that really arrived is sent back instead, not voided.'
  );
}

/** What the reader may do with a void somebody asked for. Confirming is a manager's
 *  act and never the requester's; taking it back is the requester's; a manager may
 *  also refuse it. Everyone else waits. */
export interface VoidRequestStanding {
  mayConfirm: boolean;
  mayClose: boolean;
  closeLabel: 'Take it back' | 'Decline';
  note: string;
}

export function voidRequestStanding(
  request: Pick<VoidRequest, 'requested_by_me'>,
  role: string | null,
): VoidRequestStanding {
  const manager = role === 'manager' || role === 'super_admin';
  if (request.requested_by_me) {
    return {
      mayConfirm: false,
      mayClose: true,
      closeLabel: 'Take it back',
      note: manager
        ? 'You asked for this void, so a different manager has to confirm it.'
        : 'You asked for this void. A manager has to confirm it.',
    };
  }
  if (manager) {
    return {
      mayConfirm: true,
      mayClose: true,
      closeLabel: 'Decline',
      note: 'Confirming takes this line out of the queue and the cash proof, and cannot be undone.',
    };
  }
  return {
    mayConfirm: false,
    mayClose: false,
    closeLabel: 'Decline',
    note: 'A manager has to confirm this void.',
  };
}

// ── Export ───────────────────────────────────────────────────────────────

/** Today in UTC as YYYY-MM-DD: the server compares against the UTC date. */
export function todayUtc(now: Date = new Date()): string {
  return now.toISOString().slice(0, 10);
}

export function exportProblem(asOf: string, today: string): string | null {
  if (asOf === '') return null;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(asOf)) return 'Enter the date as year-month-day.';
  if (asOf > today) return 'The report date cannot be in the future.';
  return null;
}

export function describeExport(result: {
  truncated: boolean | null;
  pending: string | null;
  disputed: string | null;
  filename: string;
}): { text: string; warn: boolean } {
  const counts =
    result.pending !== null && result.disputed !== null
      ? ` ${result.pending} pending and ${result.disputed} disputed right now.`
      : '';
  if (result.truncated === true) {
    return {
      warn: true,
      text: `Saved ${result.filename}, but the server cut the file short at its row limit — `
        + `it is NOT the complete list.${counts}`,
    };
  }
  if (result.truncated === null) {
    return {
      warn: true,
      text: `Saved ${result.filename}. The response did not say whether the file was cut `
        + `short, so compare its row count with the pending figure.${counts}`,
    };
  }
  return { warn: false, text: `Saved ${result.filename}. The file is complete.${counts}` };
}

// ── Actions (the service is passed in so the exact call can be tested) ───

export type ActionResult = { ok: true; message: string } | { ok: false; error: string };

export interface ReviewApi {
  reviewUnclaimed: (
    id: string,
    body: UnclaimedReviewBody,
  ) => Promise<{ outcome: ReviewOutcome; quiet_until: string | null }>;
}

export async function submitReview(
  api: ReviewApi,
  id: string,
  draft: ReviewDraft,
  role: string | null,
): Promise<ActionResult> {
  const problem = reviewProblem(draft, role);
  if (problem !== null) return { ok: false, error: problem };
  try {
    return { ok: true, message: describeReview(await api.reviewUnclaimed(id, buildReviewBody(draft))) };
  } catch (e: unknown) {
    return { ok: false, error: getApiErrorMessage(e, 'Could not record the review.') };
  }
}

export interface SettleApi {
  settleReturn: (returnId: string, debitId: string, mfaCode: string) => Promise<void>;
}

export async function submitSettlement(
  api: SettleApi,
  returnId: string,
  draft: SettlementDraft,
  role: string | null,
): Promise<ActionResult> {
  const problem = settlementProblem(draft, role);
  if (problem !== null) return { ok: false, error: problem };
  try {
    await api.settleReturn(returnId, draft.debitId.trim(), draft.mfaCode);
    return { ok: true, message: 'Recorded: that debit is this return leaving the account.' };
  } catch (e: unknown) {
    return { ok: false, error: stepUpErrorMessage(e, 'Could not confirm the return left.') };
  }
}

export interface VoidApi {
  requestVoid: (txId: string, reason: string) => Promise<unknown>;
}

export async function submitVoid(api: VoidApi, txId: string, reason: string): Promise<ActionResult> {
  const problem = voidProblem(reason);
  if (problem !== null) return { ok: false, error: problem };
  try {
    await api.requestVoid(txId, reason.trim());
    return {
      ok: true,
      message: 'Void requested. The line stays in the queue until a manager other than you confirms it.',
    };
  } catch (e: unknown) {
    return { ok: false, error: getApiErrorMessage(e, 'Could not ask to void this line.') };
  }
}

export interface VoidDecisionApi {
  confirmVoid: (txId: string, mfaCode: string) => Promise<unknown>;
  declineVoid: (txId: string, note: string) => Promise<unknown>;
}

export async function submitVoidConfirm(
  api: Pick<VoidDecisionApi, 'confirmVoid'>, txId: string, code: string,
): Promise<ActionResult> {
  if (code.trim().length < 6) {
    return { ok: false, error: 'Enter the current code from your authenticator.' };
  }
  try {
    await api.confirmVoid(txId, code.trim());
    return { ok: true, message: 'The line was voided.' };
  } catch (e: unknown) {
    return { ok: false, error: stepUpErrorMessage(e, 'Could not confirm this void.') };
  }
}

/** Closing a request (a manager declining, or the requester taking it back) says why. */
export async function submitVoidDecline(
  api: Pick<VoidDecisionApi, 'declineVoid'>, txId: string, note: string,
): Promise<ActionResult> {
  const problem = voidProblem(note);
  if (problem !== null) return { ok: false, error: problem };
  try {
    await api.declineVoid(txId, note.trim());
    return { ok: true, message: 'The void request was closed. The line stays in the queue.' };
  } catch (e: unknown) {
    return { ok: false, error: getApiErrorMessage(e, 'Could not close this void request.') };
  }
}
